import React, { useState, useEffect } from 'react';
import {
  X, ChevronLeft, ChevronRight, CheckCircle2, Check,
  Briefcase, Users, Landmark, ChartBar, Calendar, BarChart3,
  FileText, PaintBucket, Sparkles, HelpCircle, Inbox, CalendarClock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Resolve icon names (from screenTours.js) to components.
const ICONS = {
  Briefcase, Users, Landmark, ChartBar, Calendar, BarChart3,
  FileText, PaintBucket, Sparkles, HelpCircle, Inbox, CalendarClock,
};

/**
 * Improved guided-tour modal. Renders a polished, animated step walkthrough.
 * Props:
 *  - open, onClose
 *  - tour: { title, subtitle, icon, accent, steps: [{ title, body, bullets? }] }
 */
export const ScreenTour = ({ open, onClose, tour }) => {
  const [step, setStep] = useState(0);

  useEffect(() => { if (open) setStep(0); }, [open, tour]);

  if (!open || !tour) return null;

  const steps = tour.steps || [];
  const current = steps[step] || {};
  const isLast = step === steps.length - 1;
  const Icon = ICONS[tour.icon] || HelpCircle;
  const accent = tour.accent || 'from-blue-600 to-cyan-600';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="screen-tour-overlay"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="screen-tour-modal"
      >
        {/* Header */}
        <div className={`relative bg-gradient-to-r ${accent} px-6 pt-6 pb-8 text-white`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            data-testid="screen-tour-close"
            aria-label="Close tour"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight truncate">{tour.title}</h2>
              <p className="text-white/80 text-xs mt-0.5">{tour.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 -mt-4 bg-white rounded-t-3xl relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
              Step {step + 1} of {steps.length}
            </span>
          </div>
          <div key={step} className="animate-in fade-in slide-in-from-right-2 duration-300 min-h-[140px]">
            <h3 className="text-xl font-bold text-slate-900">{current.title}</h3>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">{current.body}</p>
            {current.bullets && (
              <ul className="mt-4 space-y-2">
                {current.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          {/* Progress dots */}
          <div className="flex items-center gap-1.5" data-testid="screen-tour-progress">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === step ? 'w-6 bg-slate-900' : 'w-2 bg-slate-300 hover:bg-slate-400'}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setStep(step - 1)} data-testid="screen-tour-back">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {!isLast ? (
              <Button size="sm" className="rounded-full" onClick={() => setStep(step + 1)} data-testid="screen-tour-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={onClose} data-testid="screen-tour-done">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Got it
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenTour;
